import { useCallback, useState } from 'react'

import { Uploads } from 'commons/types/DTO/bank/alerts/uploads'
import { TableDataQa } from 'commons/types/enums'
import { Dialog, DialogFace } from 'ui/components/Dialog'
import { Layer } from 'ui/components/Layer'
import { Link } from 'ui/components/Link'
import { PageSection, PageSectionFace } from 'ui/components/Page'
import { SortDirection, Table } from 'ui/components/Table'
import { useAlertsContext } from '~bank-alerts/context'
import { AlertsPayload } from '~bank-alerts/types'

import { columns } from './columns'
import { Filter } from './components'

import { DialogContent } from './styles'

type RelatedCompanyData = {
  subTitle: string
  companyList: Array<{ id: number; name: string }>
}

export const UploadsPendingApproval = () => {
  const { data, isTableLoading, search, onFilterChange, onPageChange } = useAlertsContext()
  const [relatedCompanyData, setRelatedCompanyData] = useState<RelatedCompanyData | null>(null)
  const [showRelatedCompanyList, setShowRelatedCompanyList] = useState(false)

  const {
    form: { uploadsPage, uploadsSize, uploadsSortBy, uploadsSortDesc },
    uploadsTotalCount,
    uploads
  } = data

  const openRelatedCompanyList = useCallback((subTitle: string, companyList: Array<{ id: number; name: string }>) => {
    setRelatedCompanyData({ subTitle, companyList })
    setShowRelatedCompanyList(true)
  }, [])

  const closeRelatedCompanyList = useCallback(() => {
    setShowRelatedCompanyList(false)
  }, [])

  const handleSort = useCallback(
    (direction: 'DESC' | 'ASC', field: string) => {
      onFilterChange(
        {
          ...search,
          uploadsSortBy: field,
          uploadsSortDesc: direction === 'DESC',
          uploadsPage: 1
        } as Partial<AlertsPayload>,
        'uploads'
      )
    },
    [search, onFilterChange]
  )

  const handlePageChange = useCallback(
    (page: number, size: number) => {
      onPageChange(page, size, 'uploads')
    },
    [onPageChange]
  )

  return (
    <div data-qa="uploads-pending-approval">
      <PageSection face={PageSectionFace.PRIMARY} title="Uploads Pending Approval" anchor="uploads-pending-approval">
        {showRelatedCompanyList && (
          <Dialog
            id="related-company-list"
            title="Related Companies"
            subTitle={relatedCompanyData?.subTitle}
            face={DialogFace.PRIMARY}
            onClose={closeRelatedCompanyList}
          >
            <DialogContent>
              <Table
                dataQa={TableDataQa.BP_ALERTS_UPLOADS_PENDING_APPROVAL}
                columns={[
                  {
                    align: 'left',
                    title: 'Relationship',
                    render: (item) => (
                      <Link to="/relationships/$id" params={{ id: item.id.toString() }}>
                        {item.relationship}
                      </Link>
                    )
                  }
                ]}
                dataSource={
                  relatedCompanyData
                    ? relatedCompanyData.companyList.map((item) => ({
                        item: {
                          relationship: item.name,
                          id: item.id
                        }
                      }))
                    : []
                }
              />
            </DialogContent>
          </Dialog>
        )}
        <Layer rounded shadowed stretch>
          <Filter />
          <Table<Uploads>
            dataQa={TableDataQa.BP_ALERTS_UPLOADS}
            columns={columns(openRelatedCompanyList)}
            dataSource={uploads.map((el) => ({
              item: el
            }))}
            loading={isTableLoading('uploads')}
            pagination={{
              page: uploadsPage || 1,
              size: uploadsSize || 10,
              onChange: handlePageChange,
              total: uploadsTotalCount
            }}
            sorting={{
              order: [{ field: uploadsSortBy, direction: uploadsSortDesc ? SortDirection.DESC : SortDirection.ASC }],
              onChange: (value) => handleSort(value.direction, value.field)
            }}
          />
        </Layer>
      </PageSection>
    </div>
  )
}
